import React, { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableCard from './DraggableCard';

function DashboardGrid({ visibleCards, onReorder, allCards, renderCard }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortableItems = useMemo(
    () => visibleCards.map(c => c.id),
    [visibleCards]
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visibleCards.findIndex(c => c.id === active.id);
    const newIndex = visibleCards.findIndex(c => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder visible cards
    const reordered = [...visibleCards];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    // Merge back with hidden cards
    const hiddenCards = allCards.filter(c => !c.visible);
    const full = [...reordered, ...hiddenCards];
    onReorder(full);
  }

  // Group visible cards by size for layout
  const { statCards, contentCards } = useMemo(() => {
    const stat = [];
    const content = [];
    visibleCards.forEach(card => {
      if (card.size === 'stat') {
        stat.push(card);
      } else {
        content.push(card);
      }
    });
    return { statCards: stat, contentCards: content };
  }, [visibleCards]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortableItems} strategy={rectSortingStrategy}>
        {/* Stat cards row */}
        {statCards.length > 0 && (
          <div className="dashboard-stat-grid mb-4">
            {statCards.map(card => (
              <DraggableCard key={card.id} id={card.id} size="stat">
                {renderCard(card.id)}
              </DraggableCard>
            ))}
          </div>
        )}

        {/* Content cards grid */}
        {contentCards.length > 0 && (
          <div className="dashboard-content-grid">
            {contentCards.map(card => (
              <DraggableCard key={card.id} id={card.id} size={card.size}>
                {renderCard(card.id)}
              </DraggableCard>
            ))}
          </div>
        )}
      </SortableContext>
    </DndContext>
  );
}

export default DashboardGrid;
